import { toast } from '@/components/toast';
import { PolotnoStoreKeys } from '@/pages/AdvancedEditor/hooks';
import { PROJECT_IDB_DRAFT_QUERY_KEY, useProjectIdb } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useProjectIdb';
import { newProjectId } from '@/pages/AdvancedEditor/types';
import { updateVideo } from '@/services/api';
import { logger } from '@/services/logger';
import { DEFAULT_VIDEO_NAME } from '@/state/presenters/presenters';
import { GET_PROJECTS_KEY, ProjectsQueryData } from '@/state/video-library/videoLibrary';
import { LibraryItem, LibraryItemType } from '@d-id/types';
import { useMutation, useQueryClient } from 'react-query';

interface UseUpdateVideoNameParams {
    item: LibraryItem;
    onError?: (originalName: string) => void;
}

export const useUpdateVideoName = ({ item, onError }: UseUpdateVideoNameParams) => {
    const queryClient = useQueryClient();
    const { idbDraft, saveIdbDraft } = useProjectIdb();

    const updateNameMutation = useMutation(
        (name: string) => updateVideo(item.id, { name: name.trim() || DEFAULT_VIDEO_NAME }, LibraryItemType.PROJECT),
        {
            onMutate: async (name: string) => {
                const prevData = queryClient.getQueryData<ProjectsQueryData>(GET_PROJECTS_KEY);
                if (prevData?.pages) {
                    const updatedPages = prevData.pages.map(page => {
                        const updatedPageItems = page.items.map(item_ => (item_.id === item.id ? { ...item_, name } : item_));
                        return { ...page, items: updatedPageItems };
                    });
                    queryClient.setQueryData(GET_PROJECTS_KEY, { ...prevData, pages: updatedPages });
                } else if (prevData?.items) {
                    const updatedItems = prevData.items.map(item_ => (item_.id === item.id ? { ...item_, name } : item_));
                    queryClient.setQueryData(GET_PROJECTS_KEY, { ...prevData, items: updatedItems });
                }
            },
            onError: () => {
                onError?.(item.name);
                toast.error({ title: 'Failed to update video name, please try again' });
                logger.error('Failed to update video name');
            },
            onSettled: () => {
                queryClient.invalidateQueries(GET_PROJECTS_KEY);
            },
        }
    );

    const updateName = async (name: string) => {
        if (item.id === newProjectId) {
            const draft = idbDraft.data;
            if (draft) {
                const updatedDraft = {
                    ...draft,
                    store: {
                        ...draft.store,
                        custom: { ...draft.store.custom, [PolotnoStoreKeys.creativeVideoName]: name },
                    },
                };
                await saveIdbDraft({ ...updatedDraft, videoName: name });
                queryClient.invalidateQueries(PROJECT_IDB_DRAFT_QUERY_KEY);
            }
        } else {
            updateNameMutation.mutate(name);
        }
    };

    return { updateName };
};
